import { useCallback } from "react";

import { useTranslations } from "next-intl";

/**
 * Safe wrapper for useTranslations that handles SSR/CSR hydration mismatches
 * and provides fallbacks when translation context is not available
 */
export function useSafeTranslations(namespace?: string) {
  // Check if we're on the server
  const isServer = typeof window === "undefined";

  // FIXED: Always call hooks at the top level, never conditionally
  // Call useTranslations unconditionally - let it throw if context isn't available
  const t = useTranslations(namespace);

  // Check if translations are working properly
  const translationError = false; // If we get here, useTranslations didn't throw

  // Always call useCallback hook - never conditionally
  // FIXED: Moved useCallback to top level of function, before any try/catch or conditional logic
  const safeT = useCallback(
    (key: string, fallback?: string) => {
      // If we had an error getting translations, return fallback
      if (translationError || !t) {
        return fallback || key;
      }

      try {
        const translated = t(key);

        // If translation is the same as key, it likely failed
        if (translated === key && fallback) {
          return fallback;
        }

        return translated;
      } catch (error) {
        if (!isServer) {
          console.warn(`Translation failed for key: ${key}`, error);
        }
        return fallback || key;
      }
    },
    [t, isServer, translationError],
  );

  return safeT;
}

/**
 * Hook that checks if translations are available
 */
export function useTranslationsAvailable(): boolean {
  try {
    useTranslations();
    return true;
  } catch {
    return false;
  }
}
